import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { cancelOnboardingIntent, getOnboardingIntent } from '../api/publicRegistrationApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { EmptyState } from '../components/EmptyState';
import { DetailField } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import { formatBillingInterval } from '../utils/systemLabels';

const { semantic } = colorTokens;

function intentStatusTone(status) {
  if (status === 'used') return 'Success';
  if (status === 'canceled' || status === 'expired') return 'Danger';
  if (status === 'submitted') return 'Info';
  if (status === 'previewed') return 'Neutral';
  return 'Neutral';
}

function JsonBlock({ value }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        borderRadius: `${radiusTokens.md}px`,
        bgcolor: semantic.bg.input,
        border: `1px solid ${semantic.border.subtle}`,
        color: semantic.text.secondary,
        fontFamily: typographyTokens.fontFamilyMono,
        fontSize: 12,
        lineHeight: '18px',
        letterSpacing: '0.01em',
        whiteSpace: 'pre',
        overflowX: 'auto',
        overflowY: 'auto',
        maxWidth: '100%',
        maxHeight: 420,
      }}
    >
      {JSON.stringify(value ?? null, null, 2)}
    </Box>
  );
}

export function OnboardingIntentDetailPage() {
  const { t } = useTranslation(['onboarding', 'common']);
  const { id } = useParams();
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [canceling, setCanceling] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getOnboardingIntent(id)
      .then(setIntent)
      .catch(() => setError(t('onboarding:intent_detail.load_failed')))
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = async () => {
    setCanceling(true);
    setError('');
    try {
      await cancelOnboardingIntent(id, { reason: cancelReason || undefined });
      load();
    } catch (err) {
      setError(safeApiErrorMessage(err, t('onboarding:intent_detail.cancel_failed')));
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (!intent) {
    return (
      <SectionCard variant="Danger" sx={{ mb: 0 }}>
        <Alert severity="error" sx={{ bgcolor: 'transparent', p: 0 }}>
          {error || t('onboarding:intent_detail.not_found')}
        </Alert>
      </SectionCard>
    );
  }

  const emptyValue = t('common:empty_value');
  const cancelable = intent.status !== 'used' && intent.status !== 'canceled';
  const hasCreatedRecords = Boolean(
    intent.created_customer_id
    || intent.created_tenant_id
    || intent.created_subscription_id,
  );

  return (
    <Box>
      <PageHeader
        type="Detail"
        title={t('onboarding:intent_detail.title', { publicId: intent.public_id })}
        subtitle={t('onboarding:intent_detail.subtitle')}
        actions={(
          <Button
            component={RouterLink}
            to="/onboarding-intents"
            variant="Ghost"
          >
            {t('onboarding:intent_detail.back_to_list')}
          </Button>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2.5}>
        <SectionCard variant="Default" sx={{ mb: 0 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2.5 }}>
            {intent.status ? (
              <StatusBadge
                status={intent.status}
                category="onboarding_intent"
                size="Sm"
                tone={intentStatusTone(intent.status)}
              />
            ) : null}
            {intent.source ? (
              <StatusBadge
                status={intent.source}
                category="generic"
                apiLabel={`${t('onboarding:intent_detail.source')}: ${intent.source}`}
                size="Sm"
                tone="Neutral"
              />
            ) : null}
            {intent.selected_billing_interval ? (
              <StatusBadge
                status={intent.selected_billing_interval}
                category="generic"
                apiLabel={`${t('onboarding:intent_detail.billing')}: ${formatBillingInterval(intent.selected_billing_interval) || intent.selected_billing_interval}`}
                size="Sm"
                tone="Neutral"
              />
            ) : null}
            {intent.selected_plan_code ? (
              <StatusBadge
                status={intent.selected_plan_code}
                category="generic"
                apiLabel={`${t('onboarding:intent_detail.plan')}: ${intent.selected_plan_code}`}
                size="Sm"
                tone="Info"
              />
            ) : null}
          </Stack>

          <Typography
            sx={{
              mb: 2,
              fontFamily: typographyTokens.fontFamily,
              fontWeight: typographyTokens.fontWeightSemibold,
              fontSize: 14,
              color: semantic.text.primary,
            }}
          >
            {t('onboarding:intent_detail.company_contact')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <DetailField
              label={t('onboarding:intent_detail.company')}
              value={intent.company_name || emptyValue}
            />
            <DetailField
              label={t('onboarding:intent_detail.contact')}
              value={`${intent.contact_name || emptyValue} · ${intent.contact_email || emptyValue}`}
            />
            <DetailField
              label={t('onboarding:intent_detail.tenant')}
              value={`${intent.tenant_name || emptyValue} (${intent.tenant_slug || emptyValue})`}
            />
            {!intent.source && (
              <DetailField
                label={t('onboarding:intent_detail.source')}
                value={emptyValue}
              />
            )}
            {!intent.selected_billing_interval && (
              <DetailField
                label={t('onboarding:intent_detail.billing')}
                value={emptyValue}
              />
            )}
            {!intent.selected_plan_code && (
              <DetailField
                label={t('onboarding:intent_detail.plan')}
                value={emptyValue}
              />
            )}
          </Box>
        </SectionCard>

        <SectionCard
          variant="Default"
          title={t('onboarding:intent_detail.modules_features')}
          sx={{ mb: 0 }}
        >
          <JsonBlock
            value={{
              selected_module_codes: intent.selected_module_codes,
              invalid_module_codes: intent.invalid_module_codes,
              selected_feature_codes: intent.selected_feature_codes,
            }}
          />
        </SectionCard>

        <SectionCard
          variant="Default"
          title={t('onboarding:intent_detail.price_snapshot')}
          sx={{ mb: 0 }}
        >
          <JsonBlock value={intent.selected_price_snapshot} />
        </SectionCard>

        <SectionCard
          variant="Default"
          title={t('onboarding:intent_detail.utm_source')}
          sx={{ mb: 0 }}
        >
          <JsonBlock
            value={{
              utm_source: intent.utm_source,
              utm_medium: intent.utm_medium,
              utm_campaign: intent.utm_campaign,
              utm_term: intent.utm_term,
              utm_content: intent.utm_content,
              referrer_url: intent.referrer_url,
              landing_url: intent.landing_url,
            }}
          />
        </SectionCard>

        <SectionCard
          variant="Default"
          title={t('onboarding:intent_detail.created_records')}
          sx={{ mb: 0 }}
        >
          {hasCreatedRecords ? (
            <Stack spacing={1.5} alignItems="flex-start">
              {intent.created_customer_id && (
                <Button
                  component={RouterLink}
                  to={`/customers/${intent.created_customer_id}`}
                  variant="Secondary"
                >
                  {t('onboarding:intent_detail.customer_link', { id: intent.created_customer_id })}
                </Button>
              )}
              {intent.created_tenant_id && (
                <Button
                  component={RouterLink}
                  to={`/tenants/${intent.created_tenant_id}`}
                  variant="Secondary"
                >
                  {t('onboarding:intent_detail.tenant_link', { id: intent.created_tenant_id })}
                </Button>
              )}
              {intent.created_subscription_id && (
                <Typography sx={{ fontSize: 13, color: semantic.text.secondary }}>
                  {t('onboarding:intent_detail.subscription_link', { id: intent.created_subscription_id })}
                </Typography>
              )}
            </Stack>
          ) : (
            <EmptyState message={t('common:no_data_found')} />
          )}
        </SectionCard>

        {cancelable && (
          <SectionCard
            variant="Danger"
            title={t('onboarding:intent_detail.cancel_intent')}
            sx={{ mb: 0 }}
          >
            <TextInput
              density="Compact"
              label={t('onboarding:intent_detail.reason_optional')}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="Danger"
              onClick={handleCancel}
              loading={canceling}
              disabled={canceling}
            >
              {t('onboarding:intent_detail.cancel_intent')}
            </Button>
          </SectionCard>
        )}
      </Stack>
    </Box>
  );
}
